import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Message } from 'semantic-ui-react'
import styled from 'styled-components'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { useAuth } from 'context'
import { useNotification } from 'hooks/toast'
import { InspectionService } from 'utility'
import { AutoTicketService } from 'utility/services/auto-ticket'
import { isActionPlanCategoryAdmin } from 'page-components/action-plan-category/utils'
import { ReassignModal } from 'page-components/auto-ticket/ReassignModal'
import {
    TICKET_STATUS,
    TICKET_STATUS_PENDING_APPROVAL,
    countApprovalReleaseTickets,
    formatTicketAssigneeLabel,
    getApiErrorMessage,
    isAnswerNegative,
    normalizeAuditTicketsByAudit,
} from 'page-components/auto-ticket/utils'
import {
    TicketStatusPill,
    TicketTable,
    TicketTableScroll,
    TicketTableWrap,
} from 'page-components/auto-ticket-rules/styles'
import { SectionCard } from './report-approval-styles'

const ApprovalTable = styled(TicketTable)`
    min-width: 720px;

    th,
    td {
        vertical-align: middle;
    }

    tr.is-active td {
        background: #f8fafc;
    }
`

const RowActions = styled.div`
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: flex-end;
`

const OutlineButton = styled.button`
    height: 30px;
    padding: 0 10px;
    border-radius: 6px;
    border: 1px solid ${p => (p.$danger ? '#fecdd3' : '#dbe3ee')};
    background: ${p => {
        if (p.$active && p.$danger) return '#fff1f2'
        if (p.$active) return '#eff6ff'
        return '#fff'
    }};
    color: ${p => (p.$danger ? '#be123c' : '#1e3a5f')};
    font-size: 12px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        border-color: ${p => (p.$danger ? '#fb7185' : '#93c5fd')};
        background: ${p => (p.$danger ? '#fff1f2' : '#eff6ff')};
    }
`

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 14px;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
    }
`

const FormCard = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    padding: 14px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

    .ui.form .field {
        margin-bottom: 10px;
    }

    .ui.form .field > label {
        font-size: 12px;
        color: #64748b;
        margin-bottom: 4px;
    }

    .ui.input > input,
    .ui.dropdown,
    textarea {
        font-size: 13px;
    }
`

const FormTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 2px;
`

const FormHint = styled.div`
    font-size: 12px;
    color: #64748b;
    margin-bottom: 12px;
    line-height: 1.4;
`

const FooterBar = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid #f1f5f9;
`

const CountText = styled.div`
    color: #475569;
    font-size: 13px;

    strong {
        color: #0f172a;
    }
`

const FooterActions = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`

const statusStyle = statusId => {
    const meta = TICKET_STATUS[statusId] || TICKET_STATUS[1]
    const map = {
        open: { bg: '#f1f5f9', color: '#475569' },
        ack: { bg: '#dbeafe', color: '#1d4ed8' },
        progress: { bg: '#fef3c7', color: '#b45309' },
        resolved: { bg: '#dcfce7', color: '#15803d' },
        closed: { bg: '#f4f4f5', color: '#52525b' },
        escalated: { bg: '#ffedd5', color: '#c2410c' },
        hold: { bg: '#f3e8ff', color: '#6d28d9' },
        pending: { bg: '#e0e7ff', color: '#3730a3' },
        cancelled: { bg: '#f4f4f5', color: '#52525b' },
    }
    return map[meta.pill] || map.open
}

const ReasonModal = ({ open, title, submitLabel, onClose, onSubmit }) => {
    const [reason, setReason] = useState('')
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) return
        setReason('')
        setError('')
        setSaving(false)
    }, [open])

    if (!open) return null

    const handleSubmit = async () => {
        const trimmed = reason.trim()
        if (trimmed.length < 5) {
            setError('Enter at least 5 characters.')
            return
        }
        setSaving(true)
        setError('')
        try {
            await onSubmit(trimmed)
            onClose()
        } catch (err) {
            setError(getApiErrorMessage(err))
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <FormTitle>{title}</FormTitle>
            <FormHint>This cancels the ticket. It will not be sent.</FormHint>
            <label
                htmlFor="cancel-ticket-reason"
                style={{
                    display: 'block',
                    marginBottom: 4,
                    fontSize: 12,
                    color: '#64748b',
                }}
            >
                Reason (min 5 characters)
            </label>
            <textarea
                id="cancel-ticket-reason"
                value={reason}
                onChange={event => setReason(event.target.value)}
                placeholder="Why is this not a real issue?"
                rows={4}
                style={{
                    width: '100%',
                    border: '1px solid #d1d5db',
                    borderRadius: 8,
                    padding: '8px 10px',
                    font: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                }}
            />
            {error && (
                <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 6 }}>
                    {error}
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <OutlineButton type="button" onClick={onClose} disabled={saving}>
                    Close
                </OutlineButton>
                <OutlineButton
                    type="button"
                    $danger
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? 'Cancelling…' : submitLabel}
                </OutlineButton>
            </div>
        </>
    )
}

export const ApprovalCorrectiveTickets = ({
    auditId,
    reloadToken = 0,
    showApprove = false,
    onApprove,
}) => {
    const { user } = useAuth()
    const { successToast, errorToast } = useNotification()
    const isAdmin = isActionPlanCategoryAdmin(user)
    const [tickets, setTickets] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [reassignId, setReassignId] = useState(null)
    const [cancelId, setCancelId] = useState(null)
    const [releasing, setReleasing] = useState(false)
    const [printing, setPrinting] = useState(false)

    const load = useCallback(async () => {
        if (!auditId) {
            setTickets([])
            return
        }
        setLoading(true)
        setError('')
        try {
            const res = await AutoTicketService.getByAudit(auditId)
            const normalized = normalizeAuditTicketsByAudit(res)
            setTickets(normalized.tickets || [])
        } catch (err) {
            setTickets([])
            setError(getApiErrorMessage(err))
        } finally {
            setLoading(false)
        }
    }, [auditId])

    useEffect(() => {
        load()
    }, [load, reloadToken])

    useEffect(() => {
        if (!reassignId && !cancelId) return
        const stillPending = ticketId =>
            tickets.some(
                ticket =>
                    ticket.ticketId === ticketId &&
                    Number(ticket.statusId) === TICKET_STATUS_PENDING_APPROVAL,
            )
        if (reassignId && !stillPending(reassignId)) setReassignId(null)
        if (cancelId && !stillPending(cancelId)) setCancelId(null)
    }, [tickets, reassignId, cancelId])

    const counts = useMemo(
        () => countApprovalReleaseTickets(tickets),
        [tickets],
    )
    const hasPending = counts.pending > 0

    const handleRelease = async () => {
        if (!auditId || releasing) return
        setReleasing(true)
        try {
            await AutoTicketService.releaseAudit(auditId)
            successToast('Tickets sent')
            await load()
        } catch (err) {
            if (err?.status === 403) {
                errorToast('Only an admin can send tickets now.')
            } else {
                errorToast(getApiErrorMessage(err))
            }
        } finally {
            setReleasing(false)
        }
    }

    const handleViewReport = async () => {
        if (!auditId || printing) return
        const previewWindow = window.open('', '_blank')
        setPrinting(true)
        try {
            const result =
                await InspectionService.getInspectionReportWithTickets(auditId)
            const reportPath =
                result?.ReportPath ||
                result?.reportPath ||
                result?.Data?.ReportPath ||
                result?.data?.ReportPath
            if (!reportPath) {
                previewWindow?.close()
                errorToast('Report with tickets is not available yet.')
                return
            }
            if (previewWindow) {
                previewWindow.location.href = reportPath
            } else {
                window.open(reportPath, '_blank', 'noopener,noreferrer')
            }
        } catch (err) {
            previewWindow?.close()
            if (!err?.auditAccessHandled) {
                errorToast(
                    'Could not generate the report with tickets. Please try again.',
                )
            }
        } finally {
            setPrinting(false)
        }
    }

    if (!auditId) return null

    return (
        <SectionCard>
            <h3 style={{ marginTop: 0 }}>Corrective-action tickets</h3>
            {error && <Message negative>{error}</Message>}
            {loading && <Message info>Loading tickets…</Message>}
            {!loading && tickets.length === 0 && !error && (
                <Message info>No corrective-action tickets for this report.</Message>
            )}
            {tickets.length > 0 && (
                <TicketTableWrap>
                    <TicketTableScroll>
                        <ApprovalTable>
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Question / answer</th>
                                    <th>Owner</th>
                                    <th style={{ textAlign: 'right' }}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {tickets.map(ticket => {
                                    const statusMeta =
                                        TICKET_STATUS[ticket.statusId] ||
                                        TICKET_STATUS[1]
                                    const pending =
                                        Number(ticket.statusId) ===
                                        TICKET_STATUS_PENDING_APPROVAL
                                    const st = statusStyle(ticket.statusId)
                                    const owner =
                                        formatTicketAssigneeLabel(ticket)
                                    const ownerOpen =
                                        reassignId === ticket.ticketId
                                    const cancelOpen =
                                        cancelId === ticket.ticketId
                                    return (
                                        <tr
                                            key={ticket.ticketId}
                                            className={
                                                ownerOpen || cancelOpen
                                                    ? 'is-active'
                                                    : undefined
                                            }
                                        >
                                            <td>
                                                <TicketStatusPill
                                                    $bg={st.bg}
                                                    $color={st.color}
                                                >
                                                    {statusMeta.label}
                                                </TicketStatusPill>
                                            </td>
                                            <td className="title-cell">
                                                <div style={{ fontWeight: 600 }}>
                                                    {ticket.questionText ||
                                                        ticket.title ||
                                                        '—'}
                                                </div>
                                                {ticket.answerText && (
                                                    <div
                                                        style={{
                                                            marginTop: 4,
                                                            color: isAnswerNegative(
                                                                ticket.answerText,
                                                            )
                                                                ? '#b91c1c'
                                                                : '#64748b',
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {ticket.answerText}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{owner}</td>
                                            <td>
                                                {pending && (
                                                    <RowActions>
                                                        <OutlineButton
                                                            type="button"
                                                            $active={ownerOpen}
                                                            onClick={() => {
                                                                if (ownerOpen) {
                                                                    setReassignId(
                                                                        null,
                                                                    )
                                                                    return
                                                                }
                                                                setCancelId(null)
                                                                setReassignId(
                                                                    ticket.ticketId,
                                                                )
                                                            }}
                                                        >
                                                            Change owner
                                                        </OutlineButton>
                                                        {isAdmin && (
                                                            <OutlineButton
                                                                type="button"
                                                                $danger
                                                                $active={
                                                                    cancelOpen
                                                                }
                                                                onClick={() => {
                                                                    if (
                                                                        cancelOpen
                                                                    ) {
                                                                        setCancelId(
                                                                            null,
                                                                        )
                                                                        return
                                                                    }
                                                                    setReassignId(
                                                                        null,
                                                                    )
                                                                    setCancelId(
                                                                        ticket.ticketId,
                                                                    )
                                                                }}
                                                            >
                                                                Not a real issue
                                                            </OutlineButton>
                                                        )}
                                                    </RowActions>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </ApprovalTable>
                    </TicketTableScroll>
                </TicketTableWrap>
            )}

            {(reassignId || cancelId) && (
                <FormGrid>
                    {reassignId && (
                        <FormCard>
                            <ReassignModal
                                inline
                                open
                                ticketId={reassignId}
                                onClose={() => setReassignId(null)}
                                onSuccess={load}
                            />
                        </FormCard>
                    )}
                    {cancelId && (
                        <FormCard>
                            <ReasonModal
                                open
                                title="Not a real issue"
                                submitLabel="Cancel ticket"
                                onClose={() => setCancelId(null)}
                                onSubmit={async reason => {
                                    await AutoTicketService.cancel(
                                        cancelId,
                                        reason,
                                    )
                                    successToast('Ticket cancelled')
                                    setCancelId(null)
                                    setReassignId(current =>
                                        current === cancelId ? null : current,
                                    )
                                    await load()
                                }}
                            />
                        </FormCard>
                    )}
                </FormGrid>
            )}

            <FooterBar>
                <CountText>
                    Approving will send <strong>{counts.pending}</strong> ticket
                    {counts.pending === 1 ? '' : 's'} ·{' '}
                    <strong>{counts.cancelled}</strong> cancelled
                </CountText>
                <FooterActions>
                    <AppSecondaryButton
                        type="button"
                        onClick={handleViewReport}
                        disabled={printing}
                    >
                        {printing ? 'Opening…' : 'View report with tickets'}
                    </AppSecondaryButton>
                    {isAdmin && hasPending && (
                        <AppPrimaryButton
                            type="button"
                            onClick={handleRelease}
                            loading={releasing}
                            disabled={releasing}
                        >
                            Send tickets now
                        </AppPrimaryButton>
                    )}
                    {showApprove && (
                        <AppPrimaryButton type="button" onClick={onApprove}>
                            Approve report
                        </AppPrimaryButton>
                    )}
                </FooterActions>
            </FooterBar>
        </SectionCard>
    )
}
